import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { LanguageProvider } from "@/lib/i18n";
import { setupChrome, wireHardwareBack, watchNetwork, registerPush, isNative } from "@/lib/native";
import { api, getUser } from "@/lib/api";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsAndConditions from "@/pages/TermsAndConditions";
import RefundPolicy from "@/pages/RefundPolicy";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminSubscribers from "@/pages/admin/AdminSubscribers";
import AdminTickets from "@/pages/admin/AdminTickets";
import AdminPlans from "@/pages/admin/AdminPlans";
import AdminRecords from "@/pages/admin/AdminRecords";
import AdminMasterData from "@/pages/admin/AdminMasterData";
import AdminGeoAccess from "@/pages/admin/AdminGeoAccess";
import AdminSecurity from "@/pages/admin/AdminSecurity";
import AdminHelpVideos from "@/pages/admin/AdminHelpVideos";
import AdminTransport from "@/pages/admin/AdminTransport";
import HelperTransport from "@/pages/helper/HelperTransport";
import DriverTransport from "@/pages/driver/DriverTransport";
import VetDashboard from "@/pages/vet/VetDashboard";
import FindVet from "@/pages/subscriber/FindVet";
import AdminVets from "@/pages/admin/AdminVets";
import AdminFeed from "@/pages/admin/AdminFeed";
import HelperFeed from "@/pages/helper/HelperFeed";
import HelpCenter from "@/pages/HelpCenter";
import SubscriberHaats from "@/pages/subscriber/SubscriberHaats";
import SubscriberCombined from "@/pages/subscriber/SubscriberCombined";
import SubscriberDashboard from "@/pages/subscriber/SubscriberDashboard";
import SubscriberHelpers from "@/pages/subscriber/SubscriberHelpers";
import SubscriberAnimals from "@/pages/subscriber/SubscriberAnimals";
import SubscriberReports from "@/pages/subscriber/SubscriberReports";
import SubscriberSettings from "@/pages/subscriber/SubscriberSettings";
import HelperDashboard from "@/pages/helper/HelperDashboard";
import HelperNewRegistration from "@/pages/helper/HelperNewRegistration";
import HelperGatepasses from "@/pages/helper/HelperGatepasses";
import HelperReports from "@/pages/helper/HelperReports";
import ProtectedRoute from "@/components/layout/ProtectedRoute";
import PWAInstallBanner from "@/components/PWAInstallBanner";
import { AuthCallback } from "@/components/auth/GoogleAuth";

function OfflineBar() {
  const [online, setOnline] = useState(true);
  useEffect(() => {
    let dispose = () => {};
    watchNetwork(setOnline).then((d) => { dispose = d; });
    return () => dispose && dispose();
  }, []);
  useEffect(() => {
    document.body.classList.toggle("am-offline", !online);
    return () => document.body.classList.remove("am-offline");
  }, [online]);
  if (online) return null;
  return (
    <div
      data-testid="offline-bar"
      className="fixed top-0 inset-x-0 z-[60] bg-amber-500 text-black text-center py-1.5 text-xs font-bold uppercase tracking-widest"
    >
      🔴 Offline · registrations will be queued and auto-synced when back online
    </div>
  );
}

function NativeBootstrap() {
  useEffect(() => {
    setupChrome();
    let unbind = () => {};
    wireHardwareBack().then((u) => { unbind = u; });
    let unbindPush = () => {};
    // Only register push tokens once the user is signed in — the endpoint
    // requires an authenticated session and there is nothing to associate the
    // token with before login.
    if (getUser()) {
      registerPush((token) => {
        if (!token) return;
        try {
          api.post("/auth/push-token", { token, platform: "android" }).catch((e) => console.debug("push-token", e));
        } catch (e) { console.debug(e); }
      }).then((u) => { unbindPush = u; });
    }
    return () => { unbind && unbind(); unbindPush && unbindPush(); };
  }, []);
  return null;
}

function AppRouter() {
  // Emergent Google Auth return: detect #session_id= synchronously during render
  // (before any route/guard runs). Read from useLocation().hash — reactive.
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
            <Route path="/refund-cancellation-policy" element={<RefundPolicy />} />

            <Route element={<ProtectedRoute roles={["super_admin"]} />}>
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/subscribers" element={<AdminSubscribers />} />
              <Route path="/admin/tickets" element={<AdminTickets />} />
              <Route path="/admin/plans" element={<AdminPlans />} />
              <Route path="/admin/records" element={<AdminRecords />} />
              <Route path="/admin/master" element={<AdminMasterData />} />
              <Route path="/admin/geo" element={<AdminGeoAccess />} />
              <Route path="/admin/security" element={<AdminSecurity />} />
              <Route path="/admin/help" element={<AdminHelpVideos />} />
              <Route path="/admin/transport" element={<AdminTransport />} />
              <Route path="/admin/vets" element={<AdminVets />} />
              <Route path="/admin/feed" element={<AdminFeed />} />
            </Route>

            <Route element={<ProtectedRoute roles={["super_admin", "subscriber", "helper"]} />}>
              <Route path="/help" element={<HelpCenter />} />
            </Route>

            <Route element={<ProtectedRoute roles={["subscriber"]} />}>
              <Route path="/subscriber" element={<SubscriberDashboard />} />
              <Route path="/subscriber/helpers" element={<SubscriberHelpers />} />
              <Route path="/subscriber/haats" element={<SubscriberHaats />} />
              <Route path="/subscriber/combined" element={<SubscriberCombined />} />
              <Route path="/subscriber/animals" element={<SubscriberAnimals />} />
              <Route path="/subscriber/reports" element={<SubscriberReports />} />
              <Route path="/subscriber/settings" element={<SubscriberSettings />} />
              <Route path="/subscriber/find-vet" element={<FindVet />} />
            </Route>

            <Route element={<ProtectedRoute roles={["helper"]} />}>
              <Route path="/helper" element={<HelperDashboard />} />
              <Route path="/helper/new-registration" element={<HelperNewRegistration />} />
              <Route path="/helper/gatepasses" element={<HelperGatepasses />} />
              <Route path="/helper/reports" element={<HelperReports />} />
              <Route path="/helper/transport" element={<HelperTransport />} />
              <Route path="/helper/feed" element={<HelperFeed />} />
            </Route>

            <Route element={<ProtectedRoute roles={["driver"]} />}>
              <Route path="/driver" element={<DriverTransport />} />
            </Route>

            <Route element={<ProtectedRoute roles={["vet"]} />}>
              <Route path="/vet" element={<VetDashboard />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
  );
}

function App() {
  return (
    <div className={`App ${isNative() ? "am-native" : ""}`}>
      <BrowserRouter>
        <LanguageProvider>
          <NativeBootstrap />
          <OfflineBar />
          <AppRouter />
          <Toaster position="top-right" richColors />
          <PWAInstallBanner />
        </LanguageProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
